/**
 * F10 · The daily line — one verse, decided by the date and nothing else.
 *
 * The feature is one line long in `05-features.md` and every word in it is a
 * constraint: *"The daily line, chosen not random. A year of verses curated once,
 * so it never surfaces something tonally wrong on a hard day. A text file, not an
 * integration."*
 *
 *  · **A file, not an integration.** Nothing here reaches the network, so the line
 *    renders on a tablet with no signal, before sign-in, and on the first paint. It
 *    is also the one thing on the kitchen screen that can never be stale.
 *  · **Not random.** `Math.random()` on a household screen means that one morning
 *    in three hundred it says something wrong for that day. Curation is the whole
 *    feature; see the header of `verses.json`.
 *  · **Deterministic by date**, so the same day shows the same line on the tablet,
 *    on Home, and on a phone in another timezone — three surfaces disagreeing about
 *    today's verse would be the tell that it is arbitrary.
 */
import verses from "./verses.json";

export type Verse = { ref: string; text: string; tone: string };

const LIST = verses.verses as Verse[];
export const TRANSLATION = verses.translation;

/**
 * Africa/Johannesburg, not the device's zone.
 *
 * Same reasoning as Home's greeting: the Front Door travels on a phone, and the
 * verse should turn over at midnight *at the house*. A family reading it in two
 * countries reads the same line.
 */
const DATE_PARTS = new Intl.DateTimeFormat("en-ZA", {
  timeZone: "Africa/Johannesburg",
  year: "numeric",
  month: "numeric",
  day: "numeric",
});

function houseDate(now: Date): { month: number; day: number } {
  const parts = DATE_PARTS.formatToParts(now);
  const get = (t: string) => Number(parts.find((p) => p.type === t)?.value ?? 1);
  return { month: get("month"), day: get("day") };
}

/**
 * A stable ordinal from month and day, on a fixed 31-day grid.
 *
 * The obvious implementation is day-of-year, and it is wrong for exactly one
 * reason: 29 February shifts every subsequent date by one, so in a leap year the
 * whole second half of the calendar shows a different verse than it did last year.
 * That is a small thing that would be noticed — a household reads this daily, and
 * "the birthday verse" becoming a different verse every fourth year is the kind of
 * quiet inconsistency that makes a feature feel arbitrary.
 *
 * A 31-day grid wastes seven indices (30 February, 31 April …) and in exchange the
 * mapping from a calendar date to a line never moves. Nothing reads the wasted
 * slots because no such date exists.
 */
function ordinal(month: number, day: number): number {
  return (month - 1) * 31 + (day - 1);
}

/**
 * Today's line. `now` is injectable so this is testable and so a screen showing
 * tomorrow's plan could show tomorrow's line without a second implementation.
 *
 * The list is shorter than a year (see the README's deviation note), so the index
 * wraps. A wrap is honest here in a way a random pick is not: it repeats a line
 * that was already judged safe, rather than reaching for one that was never read.
 */
export function lineFor(now: Date = new Date()): Verse {
  const { month, day } = houseDate(now);
  return LIST[ordinal(month, day) % LIST.length]!;
}

/** How many lines are curated. Shown in the README and the handbook, not on a screen. */
export const CURATED = LIST.length;
